/**
 * Shopify Dawn Theme Core Cart Component
 * Controls all AJAX operations for quantity inputs, cart item removal,
 * and section-rendering updates natively without conflicting elements.
 */

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('Cart-live-region-text');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  onChange(event) {
    if (event.target.classList.contains('quantity__input')) {
      this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer') ? document.getElementById('main-cart-footer').dataset.id : null,
        selector: '#main-cart-footer', // Forced full-wrapper refresh to capture pricing and dynamic checkout buttons
      }
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section).filter(Boolean),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          this.lineItemStatusElement.textContent = parsedState.errors;
          this.disableLoading(line);
          return;
        }

        const quantityElement = document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.item_count === 0) {
          const cartWrapper = document.querySelector('cart-items');
          if (cartWrapper) cartWrapper.classList.add('is-empty');
          const cartFooter = document.getElementById('main-cart-footer');
          if (cartFooter) cartFooter.classList.add('is-empty');
          const warnings = document.querySelector('.cart__warnings');
          const contents = document.querySelector('.cart__contents');
          if (warnings) warnings.classList.remove('critical-hidden');
          if (contents) contents.classList.add('critical-hidden');
        } else {
          const cartWrapper = document.querySelector('cart-items');
          if (cartWrapper) cartWrapper.classList.remove('is-empty');
          const cartFooter = document.getElementById('main-cart-footer');
          if (cartFooter) cartFooter.classList.remove('is-empty');
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace = document.getElementById(section.id);
          
          if (elementToReplace && parsedState.sections[section.section]) {
            // If updating the footer wrapper, completely update the section markup to avoid rendering drops
            if (section.id === 'main-cart-footer') {
              const htmlParser = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
              const innerContent = htmlParser.getElementById('main-cart-footer');
              if (innerContent) {
                elementToReplace.innerHTML = innerContent.innerHTML;
              }
            } else {
              // Standard inner-content behavior for line-items
              const targetNode = elementToReplace.querySelector(section.selector) || elementToReplace;
              targetNode.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          }
        });

        const updatedValue = quantityElement ? quantityElement.value : null;
        if (quantityElement && updatedValue !== quantity) {
          quantityElement.value = quantity;
        }

        this.disableLoading(line);
      })
      .catch((error) => {
        this.disableLoading(line);
        console.error('Cart Update Error:', error);
        const errors = document.getElementById('cart-errors');
        if (errors) errors.textContent = window.accessibilityStrings.cartError;
      });
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const targetElement = parsed.querySelector(selector);
    return targetElement ? targetElement.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItem = document.getElementById(`CartItem-${line}`);
    if (cartItem) {
      const spinner = cartItem.querySelector('.loading-spinner');
      if (spinner) spinner.classList.remove('hidden');
    }
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItem = document.getElementById(`CartItem-${line}`);
    if (cartItem) {
      const spinner = cartItem.querySelector('.loading-spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items');
      if (cartItems) {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}